import { create } from "zustand";
import { persist } from "zustand/middleware";
import useAuthStore from "@/store/use-auth-store";
import type { EachalabModel } from "@/api/models/api-types";

// Backend'den gelen EachalabModel'i kullanıyoruz
export type AIModel = EachalabModel;

// Legacy support için mapping function
export const mapEachalabModelToAIModel = (
  eachalabModel: EachalabModel
): AIModel => {
  return eachalabModel;
};

interface AIModelSelectionState {
  // Modal state
  isOpen: boolean;

  // Selection state
  selectedModel: AIModel | null;

  // External override list (CREATIVE_GENERATION için)
  externalModels: AIModel[] | null;

  // Filter & search state
  query: string;

  // Callback state
  onSelectCallback: ((model: AIModel) => void) | null;

  // Actions - Modal management
  openModal: (
    onSelect?: (model: AIModel) => void,
    modelsOverride?: AIModel[] | null
  ) => void;
  closeModal: () => void;

  // Actions - Model selection
  selectModel: (model: AIModel) => void;
  clearSelection: () => void;

  // Actions - Filtering
  updateQuery: (query: string) => void;
  resetFilters: () => void;

  // Actions - Complete reset
  resetState: () => void;

  // Actions - External override
  setExternalModels: (models: AIModel[] | null) => void;
}

export const useAIModelStore = create<AIModelSelectionState>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      selectedModel: null,
      query: "",
      onSelectCallback: null,
      externalModels: null,

      // Modal management
      openModal: (
        onSelect?: (model: AIModel) => void,
        modelsOverride?: AIModel[] | null
      ) =>
        set({
          isOpen: true,
          onSelectCallback: onSelect || null,
          // Eğer dışarıdan modeller verilmediyse external override'ı temizle
          externalModels: modelsOverride === undefined ? null : modelsOverride,
        }),
      closeModal: () => {
        set({
          isOpen: false,
          onSelectCallback: null,
          externalModels: null,
          query: "",
        });
        // Eğer seçim akışı (CG) sırasında kapatıldıysa draft'ı temizle
        try {
          const userId = useAuthStore.getState().user?._id;
          if (!userId) return;
          // Dinamik import ile circular bağımlılıkları önle
          import(
            "@/features/creative-generation/store/creative-generation-store"
          ).then((cg) => {
            const isSelecting =
              cg.useCreativeGenerationStore?.getState?.()?.isSelectingModel;
            if (isSelecting) {
              import("@/features/wizard/utils/draft").then((utils) => {
                utils.clearUserDraft("cg", userId);
              });
              try {
                cg.useCreativeGenerationStore.getState().reset();
              } catch {}
            }
          });
        } catch {}
      },

      // Model selection
      selectModel: (model: AIModel) => {
        const state = get();
        // Call callback if exists
        if (state.onSelectCallback) {
          state.onSelectCallback(model);
        } else {
          // Normal kullanım: callback yoksa wizard sistemini kullan
          if (model.request_schema) {
            // Model normal seçilmişse wizard'ı aç - navigate'i callback ile handle et
            // Bu durumda callback kullanılmalı
            console.warn(
              "Model selected without callback - wizard navigation should be handled by component"
            );
          }
        }
        set({
          selectedModel: model,
          isOpen: false,
          onSelectCallback: null,
        });
      },
      clearSelection: () => set({ selectedModel: null }),

      // Filtering
      updateQuery: (query: string) => set({ query }),
      resetFilters: () => set({ query: "" }),

      // Complete reset
      resetState: () =>
        set({
          query: "",
          selectedModel: null,
          isOpen: false,
          externalModels: null,
        }),

      // External override
      setExternalModels: (models: AIModel[] | null) =>
        set({ externalModels: models }),
    }),
    {
      name: "ai-model-selection-storage",
      partialize: (state) => ({
        selectedModel: state.selectedModel,
        // externalModels persist ETME: sadece geçici akış
      }),
    }
  )
);
